/**
 * Shared helper used by the portal sidebar, admin layout, and any
 * component that needs to decide whether to render admin affordances.
 * The authoritative gate is Supabase RLS (see migration
 * 20260412190000_admin_rls_hardening.sql) — this check is purely for
 * UI so non-internal users don't see admin links they can't follow.
 */
import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

export const INTERNAL_EMAIL_SUFFIX = "@roguelite.tech";

export function isInternalEmail(
  email: string | null | undefined,
): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(INTERNAL_EMAIL_SUFFIX);
}

/**
 * Fetches the account IDs of internal users (those whose email matches
 * INTERNAL_EMAIL_SUFFIX). Intended for admin pages that need to hide
 * internal-user rows by default — the result is typically fed into a
 * `.not("account_id", "in", ...)` filter. Only runs once per mount and
 * survives re-renders.
 *
 * Returns `null` while loading and an empty array if the lookup fails,
 * so callers can safely use it without blocking their own fetch.
 */
export function useInternalAccountIds(): string[] | null {
  const [ids, setIds] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("accounts")
        .select("id")
        .ilike("email", `%${INTERNAL_EMAIL_SUFFIX}`);
      if (cancelled) return;
      if (error) {
        console.warn("Failed to fetch internal account IDs:", error);
        setIds([]);
        return;
      }
      setIds((data ?? []).map((r: { id: string }) => r.id));
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return ids;
}

/** Formats a UUID list for PostgREST `in` / `not in` operators. */
export function pgIdList(ids: string[]): string {
  return `(${ids.join(",")})`;
}

/**
 * Like useInternalAccountIds but resolves to the set of licence IDs
 * owned by internal users. Used on admin pages that key off license_id
 * (activations, transfer_requests) rather than account_id.
 */
export function useInternalLicenseIds(): string[] | null {
  const [ids, setIds] = useState<string[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const accountQuery = await supabase
        .from("accounts")
        .select("id")
        .ilike("email", `%${INTERNAL_EMAIL_SUFFIX}`);
      if (cancelled) return;
      if (accountQuery.error) {
        console.warn(
          "Failed to fetch internal account IDs:",
          accountQuery.error,
        );
        setIds([]);
        return;
      }
      const accountIds = (accountQuery.data ?? []).map(
        (r: { id: string }) => r.id,
      );
      if (accountIds.length === 0) {
        setIds([]);
        return;
      }
      const licenseQuery = await supabase
        .from("licenses")
        .select("id")
        .in("account_id", accountIds);
      if (cancelled) return;
      if (licenseQuery.error) {
        console.warn(
          "Failed to fetch internal licence IDs:",
          licenseQuery.error,
        );
        setIds([]);
        return;
      }
      setIds((licenseQuery.data ?? []).map((r: { id: string }) => r.id));
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return ids;
}
